/**
 * @format
 */

import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import moment from "jalali-moment";

import Master from "../Layouts/Master";
import Loading from "../../Shared/Components/Loading";
import ModalCustom from "../../Shared/Components/ModalCustom";
import Pagination from "../../Shared/Components/Pagination";
import { error, success } from "../../Shared/Components/Toast";
import useApi from "../../Shared/hooks/api/Index";

const KIND_TEAM = 2;
const KIND_DOUBLE = 3;
const KIND_LEAGUE = 4;

const modalStyles = {
  content: {
    width: "90%",
    maxWidth: "640px",
    maxHeight: "85vh",
    overflow: "auto",
  },
};

const apiErrorMessage = (err, fallback) => {
  if (!err) return fallback;
  if (err?.data?.message) return err.data.message;
  if (err?.message) return err.message;
  const firstError = err?.data?.errors
    ? Object.values(err.data.errors).flat()[0]
    : null;
  return firstError || fallback;
};

const MyMatches = () => {
  const { search } = useLocation();
  const [loading, setLoading] = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [nationalCode, setNationalCode] = useState("");
  const [postId, setPostId] = useState("");
  const [lookupUser, setLookupUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchRegistrations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const fetchRegistrations = async () => {
    setLoading(true);
    await useApi.get(
      `/panel/match/my-registrations${search}`,
      {},
      (res) => {
        const items = res.data?.data ?? [];
        setRegistrations(items);
        setMeta(res.data?.meta ?? null);
        if (selected) {
          const updated = items.find((r) => r.id === selected.id);
          if (updated) {
            setSelected(updated);
            setDetail(updated);
          }
        }
        setLoading(false);
      },
      () => {
        setLoading(false);
        error("هنگام دریافت اطلاعات خطایی رخ داده است");
      }
    );
  };

  const openModal = (item) => {
    setSelected(item);
    setNationalCode("");
    setPostId("");
    setLookupUser(null);
    setDetail(item);
    setDetailLoading(false);
  };

  const closeModal = () => {
    setSelected(null);
    setDetail(null);
    setNationalCode("");
    setPostId("");
    setLookupUser(null);
  };

  const lookupNationalCode = async () => {
    if (!nationalCode || nationalCode.length !== 10) {
      return error("کد ملی باید ۱۰ رقم باشد");
    }

    setActionLoading(true);
    await useApi.post(
      `/panel/match/my-registrations/${selected.id}/lookup-user`,
      { national_code: nationalCode },
      (res) => {
        setLookupUser(res.data?.data ?? null);
        setActionLoading(false);
      },
      (err) => {
        setActionLoading(false);
        setLookupUser(null);
        error(apiErrorMessage(err, "کاربر یافت نشد"));
      }
    );
  };

  const addPartner = async () => {
    if (!nationalCode || nationalCode.length !== 10) {
      return error("کد ملی باید ۱۰ رقم باشد");
    }

    setActionLoading(true);
    await useApi.post(
      `/panel/match/my-registrations/${selected.id}/partner`,
      { national_code: nationalCode },
      (res) => {
        success(res.data?.message || "شریک اضافه شد");
        const payload = res.data?.data ?? res.data;
        if (payload?.id) setDetail(payload);
        setNationalCode("");
        setLookupUser(null);
        setActionLoading(false);
        fetchRegistrations();
      },
      (err) => {
        setActionLoading(false);
        error(apiErrorMessage(err, "افزودن شریک با خطا مواجه شد"));
      }
    );
  };

  const addMember = async () => {
    if (!nationalCode || nationalCode.length !== 10) {
      return error("کد ملی باید ۱۰ رقم باشد");
    }
    if (!postId) {
      return error("لطفاً سمت را انتخاب کنید");
    }

    setActionLoading(true);
    await useApi.post(
      `/panel/match/my-registrations/${selected.id}/members`,
      { national_code: nationalCode, post_id: Number(postId) },
      (res) => {
        success(res.data?.message || "عضو اضافه شد");
        const payload = res.data?.data ?? res.data;
        if (payload?.id) setDetail(payload);
        setNationalCode("");
        setPostId("");
        setLookupUser(null);
        setActionLoading(false);
        fetchRegistrations();
      },
      (err) => {
        setActionLoading(false);
        error(apiErrorMessage(err, "افزودن عضو با خطا مواجه شد"));
      }
    );
  };

  const updateCaptainPost = async (newPostId) => {
    if (!newPostId) return;

    setActionLoading(true);
    await useApi.put(
      `/panel/match/my-registrations/${selected.id}/my-post`,
      { post_id: Number(newPostId) },
      (res) => {
        success(res.data?.message || "سمت شما ثبت شد");
        const payload = res.data?.data ?? res.data;
        if (payload?.id) setDetail(payload);
        setActionLoading(false);
        fetchRegistrations();
      },
      (err) => {
        setActionLoading(false);
        error(apiErrorMessage(err, "ثبت سمت با خطا مواجه شد"));
      }
    );
  };

  const removeMember = async (memberRegisterId) => {
    setActionLoading(true);
    await useApi.delete(
      `/panel/match/my-registrations/${selected.id}/members/${memberRegisterId}`,
      {},
      (res) => {
        success(res.data?.message || "عضو حذف شد");
        const payload = res.data?.data ?? res.data;
        if (payload?.id) setDetail(payload);
        setActionLoading(false);
        fetchRegistrations();
      },
      (err) => {
        setActionLoading(false);
        error(apiErrorMessage(err, "حذف عضو با خطا مواجه شد"));
      }
    );
  };

  const isDoubleKind = (item) =>
    item?.match_kind_name === "دوبل" || Number(item?.match_kind_id) === KIND_DOUBLE;

  const isTeamKind = (item) =>
    item?.match_kind_name === "تیمی" || Number(item?.match_kind_id) === KIND_TEAM;

  const isLeagueKind = (item) =>
    item?.match_kind_name === "لیگ" || Number(item?.match_kind_id) === KIND_LEAGUE;

  const isTeamLikeKind = (item) => isTeamKind(item) || isLeagueKind(item);

  const canManage = (item) => item?.can_manage || item?.is_captain;

  const renderActionButton = (item) => {
    if (!canManage(item)) {
      return <span className="text-muted f-12">—</span>;
    }

    if (isDoubleKind(item) && !item.is_complete) {
      return (
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => openModal(item)}
        >
          معرفی شریک
        </button>
      );
    }

    if (isTeamLikeKind(item)) {
      return (
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => openModal(item)}
        >
          {item.is_complete ? "مشاهده تیم" : "مدیریت تیم"}
        </button>
      );
    }

    return <span className="text-muted f-12">—</span>;
  };

  const isGroupParticipantOnly = (item) =>
    (isTeamLikeKind(item) || isDoubleKind(item)) && !canManage(item);

  const renderStatusCell = (item) => {
    if (isGroupParticipantOnly(item)) {
      return <span className="badge badge-success">ثبت‌نام شده</span>;
    }

    return (
      <>
        <span
          className={`badge ${
            item.is_complete ? "badge-success" : "badge-warning"
          }`}
        >
          {item.is_complete ? "کامل" : "ناقص"}
        </span>
        {!item.is_complete && (
          <div className="f-11 text-muted mt-1">{item.completion_message}</div>
        )}
      </>
    );
  };

  const renderDoubleModal = () => {
    const active = detail || selected;
    return (
    <>
      <h5 className="mb-3">معرفی شریک (دوبل)</h5>
      <p className="f-13">{active?.completion_message}</p>
      <div className="form-group">
        <label>کد ملی شریک</label>
        <div className="d-flex flex-row">
          <input
            type="text"
            className="form-control"
            maxLength={10}
            value={nationalCode}
            onChange={(e) => {
              setNationalCode(e.target.value.replace(/\D/g, ""));
              setLookupUser(null);
            }}
          />
          <button
            type="button"
            className="btn btn-secondary mx-2"
            onClick={lookupNationalCode}
            disabled={actionLoading}
          >
            بررسی
          </button>
        </div>
      </div>
      {lookupUser && (
        <div className="alert alert-info f-13">
          {lookupUser.name} — {lookupUser.national_card_id}
        </div>
      )}
      {active?.members?.length > 0 && (
        <table className="table table-sm table-dark mt-3">
          <thead>
            <tr>
              <th>نام</th>
              <th>کد ملی</th>
              <th>نقش</th>
            </tr>
          </thead>
          <tbody>
            {active.members.map((member) => (
              <tr key={member.register_id}>
                <td>{member.name}</td>
                <td>{member.national_card_id}</td>
                <td>{member.is_captain ? "کاپیتان" : "شریک"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="d-flex justify-content-end mt-3">
        <button type="button" className="btn btn-light mx-2" onClick={closeModal}>
          بستن
        </button>
        <button
          type="button"
          className="btn btn-success"
          onClick={addPartner}
          disabled={actionLoading || active?.is_complete}
        >
          {actionLoading ? "..." : "افزودن شریک"}
        </button>
      </div>
    </>
    );
  };

  const renderTeamModal = () => {
    const active = detail || selected;
    return (
    <>
      <h5 className="mb-3">{isLeagueKind(active) ? "مدیریت تیم لیگ" : "مدیریت تیم"}</h5>
      {active?.club_title && (
        <p className="f-13 text-info">باشگاه: {active.club_title}</p>
      )}
      <p className="f-13">{active?.completion_message}</p>

      {!active?.my_post_id && (
        <div className="form-group mb-3">
          <label>سمت شما (کاپیتان)</label>
          <select
            className="form-control"
            value={active?.my_post_id || ""}
            onChange={(e) => updateCaptainPost(e.target.value)}
            disabled={actionLoading}
          >
            <option value="">انتخاب سمت</option>
            {active?.available_posts
              ?.filter((post) => post.remaining > 0)
              .map((post) => (
                <option key={post.post_id} value={post.post_id}>
                  {post.title} ({post.remaining} جای خالی)
                </option>
              ))}
          </select>
        </div>
      )}

      <table className="table table-sm table-dark">
        <thead>
          <tr>
            <th>نام</th>
            <th>کد ملی</th>
            <th>سمت</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {active?.members?.map((member) => (
            <tr key={member.register_id}>
              <td>
                {member.name}
                {member.is_captain ? " (کاپیتان)" : ""}
              </td>
              <td>{member.national_card_id}</td>
              <td>{member.post_title || "—"}</td>
              <td>
                {member.can_remove && (
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => removeMember(member.register_id)}
                    disabled={actionLoading}
                  >
                    حذف
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {!active?.is_complete && (
        <div className="border-top pt-3 mt-3">
          <h6>افزودن عضو</h6>
          <div className="form-group">
            <label>کد ملی</label>
            <div className="d-flex flex-row">
              <input
                type="text"
                className="form-control"
                maxLength={10}
                value={nationalCode}
                onChange={(e) => {
                  setNationalCode(e.target.value.replace(/\D/g, ""));
                  setLookupUser(null);
                }}
              />
              <button
                type="button"
                className="btn btn-secondary mx-2"
                onClick={lookupNationalCode}
                disabled={actionLoading}
              >
                بررسی
              </button>
            </div>
          </div>
          {lookupUser && (
            <div className="alert alert-info f-13">
              {lookupUser.name} — {lookupUser.national_card_id}
            </div>
          )}
          <div className="form-group">
            <label>سمت</label>
            <select
              className="form-control"
              value={postId}
              onChange={(e) => setPostId(e.target.value)}
            >
              <option value="">انتخاب سمت</option>
              {active?.available_posts
                ?.filter((post) => post.remaining > 0)
                .map((post) => (
                  <option key={post.post_id} value={post.post_id}>
                    {post.title} ({post.remaining} جای خالی)
                  </option>
                ))}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-success"
            onClick={addMember}
            disabled={actionLoading}
          >
            {actionLoading ? "..." : "افزودن عضو"}
          </button>
        </div>
      )}

      <div className="d-flex justify-content-end mt-3">
        <button type="button" className="btn btn-light" onClick={closeModal}>
          بستن
        </button>
      </div>
    </>
    );
  };

  return loading ? (
    <Loading />
  ) : (
    <Master>
      <div className="d-flex flex-row justify-content-between border-bottom pb-2 my-3">
        <div className="d-flex flex-row mt-2">
          <i className="fad fa-list f-20 text-secondary mx-2"></i>
          <h1 className="text-secondary f-13 font-weight-bold mt-1 mb-0 p-0">
            مسابقات من
          </h1>
        </div>
      </div>
      <div className="w-100 overflow-auto">
        {registrations.length > 0 ? (
          <table className="table table-hover">
            <thead>
              <tr>
                <th>#</th>
                <th>عنوان</th>
                <th>نوع</th>
                <th>رده سنی</th>
                <th>وضعیت</th>
                <th>شروع</th>
                <th>تاریخ ثبت‌نام</th>
                <th>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {registrations.map((item) => (
                <tr key={item.id}>
                  <th>{item.id}</th>
                  <td>{item?.match?.title}</td>
                  <td>{item?.match_kind_name}</td>
                  <td>{item?.match_age_title}</td>
                  <td>{renderStatusCell(item)}</td>
                  <td>
                    {moment(item?.match?.start ?? "1990-01-01", "YYYY-MM-DD")
                      .locale("fa")
                      .format("YYYY/MM/DD")}
                  </td>
                  <td>
                    {moment(item.created_at ?? "1990-01-01", "YYYY-MM-DD")
                      .locale("fa")
                      .format("YYYY/MM/DD")}
                  </td>
                  <td>{renderActionButton(item)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="d-flex flex-row justify-content-center border p-3 my-3">
            <i className="fad fa-info-circle f-16 mx-2 text-secondary"></i>
            <span className="f-13 font-weight-bold text-secondary">
              هنوز در مسابقه‌ای ثبت‌نام نکرده‌اید
            </span>
          </div>
        )}
      </div>
      {(meta?.last_page ?? meta?.pagination?.total_pages) > 1 && (
        <div className="w-100 mt-4 d-flex">
          <Pagination
            totalPage={meta?.last_page ?? meta?.pagination?.total_pages ?? 1}
            basePath="/panel/profile/match-registered"
          />
        </div>
      )}

      <ModalCustom
        modalIsOpen={!!selected}
        onRequestClose={closeModal}
        styles={modalStyles}
      >
        {detailLoading ? (
          <div className="text-center p-4">
            <div className="spinner-border text-light" role="status" />
          </div>
        ) : selected && (isDoubleKind(detail) || isDoubleKind(selected)) ? (
          renderDoubleModal()
        ) : selected && (isTeamLikeKind(detail || selected)) ? (
          renderTeamModal()
        ) : (
          <div className="p-3">
            <p>ثبت‌نام انفرادی نیازی به تکمیل اطلاعات ندارد.</p>
            <button type="button" className="btn btn-light" onClick={closeModal}>
              بستن
            </button>
          </div>
        )}
      </ModalCustom>
    </Master>
  );
};

export default MyMatches;
